import { useRef, useState } from "react";
import { UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";

const EMPTY = { name: "", name_kana: "", contact_person: "", tel: "", email: "", postal_code: "", address: "", notes: "", is_active: true };
const FIELDS = [
  ["name", "名称 *", "text"], ["name_kana", "フリガナ", "text"],
  ["contact_person", "担当者", "text"], ["tel", "電話番号", "tel"],
  ["email", "メールアドレス", "email"], ["postal_code", "郵便番号", "text"],
];
const inputCls = "mt-2 min-w-0 w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

export const CustomerCreateDialog = ({ onClose, onCreated, onReturnFocus }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const submitting = useRef(false);
  const set = key => e => { setForm(f => ({ ...f, [key]: e.target.value })); setError(""); };
  const save = async e => {
    e.preventDefault();
    if (submitting.current) return;
    if (!form.name.trim()) { setError("お客様の名称を入力してください。"); return; }
    submitting.current = true;
    setSaving(true); setError("");
    try {
      const { data } = await api.post("/admin/customers", { ...form, name: form.name.trim(), email: form.email.trim() });
      onCreated(data);
    } catch (err) {
      setError(err.response?.data?.detail ? formatApiErrorDetail(err.response.data.detail) : "顧客を登録できませんでした。入力内容を確認して、もう一度お試しください。");
    } finally { submitting.current = false; setSaving(false); }
  };

  return <Dialog open onOpenChange={open => { if (!open && !submitting.current) onClose(); }}>
    <DialogContent data-testid="estimate-customer-registration-dialog" aria-describedby="estimate-customer-registration-description"
      onCloseAutoFocus={e => { e.preventDefault(); requestAnimationFrame(() => onReturnFocus?.()); }}
      onEscapeKeyDown={e => { if (submitting.current) e.preventDefault(); }}
      onPointerDownOutside={e => e.preventDefault()}
      className="max-h-[90dvh] w-[calc(100%-2rem)] max-w-xl overflow-y-auto border-line bg-coal text-white sm:rounded-none">
      <DialogTitle data-testid="estimate-customer-registration-title" className="flex items-center gap-2 pr-5 text-base font-bold sm:text-lg"><UserPlus className="h-5 w-5 shrink-0 text-accent" />顧客を新規登録</DialogTitle>
      <DialogDescription id="estimate-customer-registration-description" className="sr-only">見積書のお客様情報</DialogDescription>
      <form data-testid="estimate-customer-registration-form" onSubmit={save} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map(([key, label, type]) => <label key={key} className="block min-w-0 text-xs text-gray-400">{label}
            <input data-testid={`estimate-customer-registration-${key.replaceAll("_", "-")}`} type={type} required={key === "name"} maxLength={key === "name" ? 200 : undefined} value={form[key]} onChange={set(key)} disabled={saving} className={inputCls} />
          </label>)}
        </div>
        <label className="block text-xs text-gray-400">住所<input data-testid="estimate-customer-registration-address" value={form.address} onChange={set("address")} disabled={saving} className={inputCls} /></label>
        <label className="block text-xs text-gray-400">備考<textarea data-testid="estimate-customer-registration-notes" rows={2} value={form.notes} onChange={set("notes")} disabled={saving} className={inputCls} /></label>
        {error && <p data-testid="estimate-customer-registration-error" role="alert" className="text-sm text-red-300">{error}</p>}
        <div className="flex flex-col-reverse gap-3 sm:flex-row">
          <button data-testid="estimate-customer-registration-cancel" type="button" disabled={saving} onClick={onClose} className="border border-line px-4 py-3 text-sm text-gray-300 transition-colors hover:border-accent hover:text-accent disabled:opacity-40">登録せず見積に戻る</button>
          <button data-testid="estimate-customer-registration-submit" type="submit" disabled={saving} className="flex-1 bg-accent px-4 py-3 text-sm font-bold text-ink transition-colors hover:bg-accent-hover disabled:opacity-40">{saving ? "登録中…" : "登録して見積に戻る"}</button>
        </div>
      </form>
    </DialogContent>
  </Dialog>;
};